import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { adminAddonService } from "../../../services/api/settings";
import {
  ServiceType,
  CreateAddonRequest,
  AddonCategory,
  AddonBillingType,
} from "../../../store/types/settings";

const initialFormState: Partial<CreateAddonRequest> = {
  serviceType: "RECRUITER",
  name: "",
  displayName: "",
  description: "",
  price: 0,
  category: "ENTERPRISE",
  billingType: "YEARLY",
  id: "", // direct
  start: false,
  pro: false,
  enterprise: false,
  vip: false,
};

const CreateAddon: React.FC = () => {
  const [formData, setFormData] =
    useState<Partial<CreateAddonRequest>>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [hasPlanSpecificPricing, setHasPlanSpecificPricing] = useState(false);
  const [planSpecificPricing, setPlanSpecificPricing] = useState<
    { planId: string; price: number; currency?: string }[]
  >([{ planId: "", price: 0, currency: "NGN" }]);

  const handlePlanPricingChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    const updated = [...planSpecificPricing];
    updated[index] = { ...updated[index], [field]: value };
    setPlanSpecificPricing(updated);
  };

  const addPlanPricingRow = () => {
    setPlanSpecificPricing([
      ...planSpecificPricing,
      { planId: "", price: 0, currency: "NGN" },
    ]);
  };

  const removePlanPricingRow = (index: number) => {
    const updated = planSpecificPricing.filter((_, i) => i !== index);
    setPlanSpecificPricing(updated);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev: Partial<CreateAddonRequest>) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? Number(value)
            : value,
    }));
  };

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as ServiceType;
    setFormData({
      ...initialFormState,
      serviceType: selectedType,
      category: selectedType === "CONSOLE" ? "INTEGRATION" : "ENTERPRISE",
      billingType: selectedType === "CONSOLE" ? "ONE_TIME" : "YEARLY",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Cleanup payload based on serviceType
      const payload: any = { serviceType: formData.serviceType };

      if (formData.serviceType === "RECRUITER") {
        payload.name = formData.name;
        payload.displayName = formData.displayName;
        payload.description = formData.description;
        payload.price = formData.price;
        payload.category = formData.category;
        payload.billingType = formData.billingType;
        if (hasPlanSpecificPricing) {
          payload.planSpecificPricing = planSpecificPricing;
        }
      } else if (formData.serviceType === "DIRECT") {
        payload.id = formData.id;
        payload.name = formData.name;
        payload.price = formData.price;
        payload.start = formData.start;
        payload.pro = formData.pro;
        payload.enterprise = formData.enterprise;
        payload.vip = formData.vip;
        if (hasPlanSpecificPricing) {
          payload.planSpecificPricing = planSpecificPricing;
        }
      } else if (formData.serviceType === "CONSOLE") {
        payload.name = formData.name;
        payload.price = formData.price;
        payload.category = formData.category;
        payload.billingType = formData.billingType;
      }

      await adminAddonService.create(payload);

      // Reset form on success keeping the selected service type
      setFormData({
        ...initialFormState,
        serviceType: formData.serviceType,
        category:
          formData.serviceType === "CONSOLE" ? "INTEGRATION" : "ENTERPRISE",
        billingType: formData.serviceType === "CONSOLE" ? "ONE_TIME" : "YEARLY",
      });
      setHasPlanSpecificPricing(false);
      setPlanSpecificPricing([{ planId: "", price: 0, currency: "NGN" }]);
    } catch (error) {
      console.error("Error creating add-on:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full py-5">
      <div className="max-w-[800px] mx-auto px-3">
        <div className="flex border-b border-border text-20 pt-3 mb-6">
          <div className="border-b-[3px] border-secondary pb-1 cursor-pointer mr-8">
            <p className="font-[400]">Create System Add-on</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border-b border-gray-200 pb-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Service Type
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleServiceTypeChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                required
              >
                <option value="RECRUITER">AI Recruiter (Features)</option>
                <option value="DIRECT">Direct (Modules)</option>
                <option value="CONSOLE">
                  Console (Integrations & Deployments)
                </option>
              </select>
            </div>

            {/* Recruiter Fields */}
            {formData.serviceType === "RECRUITER" && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      System Name (e.g. CUSTOM_SLA_AGREEMENT)
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      placeholder="UPPERCASE_WITH_UNDERSCORES"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      placeholder="Custom SLA Agreement"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    placeholder="Tailored SLA with guaranteed 99.9% uptime"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (in Kobo)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">-- SELECT CATEGORY --</option>
                      <option value="STARTER">STARTER</option>
                      <option value="GROWTH">GROWTH</option>
                      <option value="SCALE">SCALE</option>
                      <option value="ENTERPRISE">ENTERPRISE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Type
                    </label>
                    <select
                      name="billingType"
                      value={formData.billingType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="YEARLY">YEARLY</option>
                      <option value="MONTHLY">MONTHLY</option>
                      <option value="ONE_TIME">ONE_TIME</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 border-t border-gray-100 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={hasPlanSpecificPricing}
                      onChange={(e) =>
                        setHasPlanSpecificPricing(e.target.checked)
                      }
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable Plan Specific Pricing?
                    </span>
                  </label>

                  {hasPlanSpecificPricing && (
                    <div className="space-y-4">
                      {planSpecificPricing.map((pricing, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 animate-in fade-in slide-in-from-top-2"
                        >
                          <div className="flex-1">
                            <input
                              type="text"
                              value={pricing.planId}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "planId",
                                  e.target.value,
                                )
                              }
                              placeholder="Plan ID (e.g. premium-plan-id)"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              required={hasPlanSpecificPricing}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="number"
                              value={pricing.price}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "price",
                                  Number(e.target.value),
                                )
                              }
                              placeholder="Price in Kobo"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              required={hasPlanSpecificPricing}
                              min={0}
                            />
                          </div>
                          <div className="w-24">
                            <input
                              type="text"
                              value={pricing.currency || ""}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "currency",
                                  e.target.value,
                                )
                              }
                              placeholder="NGN"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePlanPricingRow(index)}
                            disabled={planSpecificPricing.length === 1}
                            className={`p-2 rounded-lg transition-colors ${
                              planSpecificPricing.length === 1
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-red-500 hover:bg-red-50"
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addPlanPricingRow}
                        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Plan Pricing
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Direct Fields */}
            {formData.serviceType === "DIRECT" && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID (slug, e.g. executive-search)
                    </label>
                    <input
                      type="text"
                      name="id"
                      value={formData.id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      placeholder="kebab-case-id"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Module Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      placeholder="Executive Search Module"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (in Kobo)
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    min={0}
                  />
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Available For Tiers
                  </label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="start"
                        checked={formData.start}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-gray-700">Start (Basic)</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="pro"
                        checked={formData.pro}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-gray-700">Pro</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="enterprise"
                        checked={formData.enterprise}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-gray-700">Enterprise</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="vip"
                        checked={formData.vip}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                      />
                      <span className="text-gray-700">VIP</span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 border-t border-gray-100 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={hasPlanSpecificPricing}
                      onChange={(e) =>
                        setHasPlanSpecificPricing(e.target.checked)
                      }
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable Plan Specific Pricing?
                    </span>
                  </label>

                  {hasPlanSpecificPricing && (
                    <div className="space-y-4">
                      {planSpecificPricing.map((pricing, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 animate-in fade-in slide-in-from-top-2"
                        >
                          <div className="flex-1">
                            <input
                              type="text"
                              value={pricing.planId}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "planId",
                                  e.target.value,
                                )
                              }
                              placeholder="Plan ID (e.g. basic-direct-id)"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              required={hasPlanSpecificPricing}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="number"
                              value={pricing.price}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "price",
                                  Number(e.target.value),
                                )
                              }
                              placeholder="Price in Kobo"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              required={hasPlanSpecificPricing}
                              min={0}
                            />
                          </div>
                          <div className="w-24">
                            <input
                              type="text"
                              value={pricing.currency || ""}
                              onChange={(e) =>
                                handlePlanPricingChange(
                                  index,
                                  "currency",
                                  e.target.value,
                                )
                              }
                              placeholder="NGN"
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePlanPricingRow(index)}
                            disabled={planSpecificPricing.length === 1}
                            className={`p-2 rounded-lg transition-colors ${
                              planSpecificPricing.length === 1
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-red-500 hover:bg-red-50"
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addPlanPricingRow}
                        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Plan Pricing
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Console Fields */}
            {formData.serviceType === "CONSOLE" && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name (e.g. Corporate Email Integration)
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (in Kobo)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="INTEGRATION">INTEGRATION</option>
                      <option value="DEPLOYMENT_COST">DEPLOYMENT_COST</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Type
                    </label>
                    <select
                      name="billingType"
                      value={formData.billingType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="ONE_TIME">ONE_TIME</option>
                      <option value="YEARLY">YEARLY</option>
                      <option value="MONTHLY">MONTHLY</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto px-8 py-3 bg-secondary text-white font-medium rounded-lg hover:bg-secondary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Creating Add-on..." : "Create Add-on Feature"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAddon;
